// @ts-nocheck
// RLevents migration: public.role_assignments was dropped.
// This hook keeps its surface (so consumers don't break) but no longer
// reads from or writes to role_assignments. profile_memberships still works.
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { updateProfileActiveMembership } from '@/utils/membershipUtils';

interface RoleAssignment {
  id: string;
  role_slug: string;
  is_active: boolean;
  assigned_at: string;
}

interface ProfileMembership {
  id: string;
  membership_slug: string;
  is_active: boolean;
  assigned_at: string;
  expires_at?: string;
}

export const useRoleAssignments = (profileId: string | null) => {
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
  const [profileMemberships, setProfileMemberships] = useState<ProfileMembership[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignments = async () => {
    if (!profileId) return;

    try {
      setIsLoading(true);
      setError(null);

      // role_assignments table dropped — always empty in RLevents.
      setRoleAssignments([]);

      const { data: memberships, error: membershipsError } = await supabase
        .from('profile_memberships')
        .select('*')
        .eq('profile_id', profileId)
        .eq('is_active', true);

      if (membershipsError) {
        console.error('❌ fetchAssignments: Profile memberships error:', membershipsError);
      }

      setProfileMemberships(memberships || []);
    } catch (err) {
      console.error('💥 fetchAssignments: Exception:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch assignments');
    } finally {
      setIsLoading(false);
    }
  };

  // No-op: roles now live in user_roles (managed via Supabase admin / migrations).
  const assignRole = async (_roleSlug: string) => {
    toast.info('Role management has moved to user_roles — manage in Supabase.');
    return false;
  };

  const removeRole = async (_roleSlug: string) => {
    toast.info('Role management has moved to user_roles — manage in Supabase.');
    return false;
  };

  const assignMembership = async (membershipSlug: string) => {
    if (!profileId) return false;

    try {
      const { data: existingMembership } = await supabase
        .from('profile_memberships')
        .select('id, is_active, expires_at')
        .eq('profile_id', profileId)
        .eq('membership_slug', membershipSlug)
        .maybeSingle();

      let error: any = null;
      if (existingMembership) {
        const result = await supabase
          .from('profile_memberships')
          .update({
            is_active: true,
            expires_at: null,
            started_at: new Date().toISOString(),
            assigned_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingMembership.id)
          .select();
        error = result.error;
      } else {
        const result = await supabase
          .from('profile_memberships')
          .insert({ profile_id: profileId, membership_slug: membershipSlug, is_active: true })
          .select();
        error = result.error;
      }

      if (error) throw error;

      await updateProfileActiveMembership(profileId);
      await fetchAssignments();
      toast.success('Membership assigned successfully');
      return true;
    } catch (err) {
      console.error('💥 assignMembership: Exception caught:', err);
      toast.error('Failed to assign membership');
      return false;
    }
  };

  const removeMembership = async (membershipSlug: string) => {
    if (!profileId) return false;

    try {
      const { error } = await supabase
        .from('profile_memberships')
        .update({ is_active: false })
        .eq('profile_id', profileId)
        .eq('membership_slug', membershipSlug);

      if (error) throw error;

      await updateProfileActiveMembership(profileId);
      await fetchAssignments();
      toast.success('Membership removed successfully');
      return true;
    } catch (err) {
      console.error('💥 removeMembership: Exception:', err);
      toast.error('Failed to remove membership');
      return false;
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [profileId]);

  return {
    roleAssignments,
    profileMemberships,
    isLoading,
    error,
    assignRole,
    removeRole,
    assignMembership,
    removeMembership,
    refreshAssignments: fetchAssignments,
  };
};
